<template>
  <div class="main-content">
    <div class="detail-table">
      <div>
        <div class="detail-item-title">Kind</div>
        <div class="detail-item">
          <template v-if="isEdit">
            {{ contents.type | typeName }}
          </template>
          <template v-else>
            <ValidationProvider rules="required" v-slot="{ errors }">
              <SelectScheduleTypeInput :isAddSchedule="true" v-model="contents.type"/>
              <div class="error-text">{{ errors[0] }}</div>
            </ValidationProvider>
          </template>
        </div>
      </div>
      <div>
        <div class="detail-item-title">Campus</div>
        <div class="detail-item">
          <template v-if="isLesson">
            {{ contents.campus.name }}
          </template>
          <template v-else>
            <ValidationProvider rules="required" v-slot="{ errors }">
              <SelectCampusInput v-model="contents.campus" />
              <div class="error-text">{{ errors[0] }}</div>
            </ValidationProvider>
          </template>
        </div>
      </div>
      <div>
        <div class="detail-item-title">Date</div>
        <div class="detail-item">
          <ValidationProvider rules="required" v-slot="{ errors }">
            <DateInput v-model="contents.date" />
            <div class="error-text">{{ errors[0] }}</div>
          </ValidationProvider>
        </div>
      </div>
      <div class="detail-item" v-if="contents.type === 'waiting' || isLesson">
        <div class="detail-item-title">Period</div>
        <ValidationProvider rules="required" v-slot="{ errors }">
          <select class="time" v-model="contents.period">
            <option></option>
            <option v-for="p of [1,2,3,4,5,6]" :key="p" :value="p">Period {{ p }}</option>
          </select>
          <div class="error-text">{{ errors[0] }}</div>
        </ValidationProvider>
      </div>
      <div v-else>
        <div class="detail-item-title">From</div>
        <div class="detail-item">
          <ValidationProvider rules="required|before_date:@afterDate" v-slot="{ errors }">
            <TimeInput v-model="contents.from" />
            <div class="error-text">{{ errors[0] }}</div>
          </ValidationProvider>
        </div>
        <div class="detail-item-title">To</div>
        <div class="detail-item">
          <ValidationProvider name="afterDate" rules="required" v-slot="{ errors }">
            <TimeInput v-model="contents.to" />
            <div class="error-text">{{ errors[0] }}</div>
          </ValidationProvider>
        </div>
        <div v-if="this.contents.from && this.contents.to">
          <div class="detail-item-title">Participant Time</div>
          <div class="detail-item participantTime">
            <ValidationProvider
              :rules="`max_time:${scheduleTimeOfMinutes}`"
              v-slot="{ errors }"
            >
              <select v-model="contents.participantTime">
                <option v-for="({ time, label }) of participantTimes" :key="time" :value="time">
                  {{ label }}
                </option>
              </select>
              <div class="error-text">{{ errors[0] }}</div>
            </ValidationProvider>
          </div>
        </div>
      </div>
      <div>
        <div class="detail-item-title">Memo</div>
        <div class="detail-item">
          <textarea rows="5" v-model="contents.memo"></textarea>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  Component, Prop, Vue, Watch,
} from 'vue-property-decorator'
import { extend, ValidationProvider } from 'vee-validate';
import _ from 'lodash'
import moment, { Moment } from 'moment'
import * as rules from 'vee-validate/dist/rules';
import DateInput from '@/components/parts/inputItem/DateInput.vue'
import { Schedule, scheduleType, ScheduleTypes } from '@/store/schedules/types';
import SelectScheduleTypeInput from '@/components/parts/inputItem/SelectScheduleTypeInput.vue';
import SelectCampusInput from '@/components/parts/inputItem/SelectCampusInput.vue';
import TimeInput from '@/components/parts/inputItem/TimeInput.vue'

// バリデーションルール
extend('required', rules.required);
extend('required', { message: 'required.' });
extend('before_date', {
  params: ['after'],
  message: 'Fix time before "To"',
  validate(value, args) {
    if (_.isArray(args)) {
      throw Error('vee-validate before_dateでリスト型が渡されました')
    }
    const { after } = args
    return value < after
  },
})
extend('max_time', rules.max_value);
extend('max_time', { message: 'Please fill in within a specific time.' });

@Component({
  components: {
    ValidationProvider,
    DateInput,
    TimeInput,
    SelectScheduleTypeInput,
    SelectCampusInput,
  },

  filters: {
    /**
     * スケジュールタイプの名前を返す
     * @param {scheduleType} type スケジュール種別
     * @returns {string} スケジュール種別名
     */
    typeName(type: scheduleType): string {
      return ScheduleTypes[type]
    },
  },
})
export default class SchedulesInputForm extends Vue {
  @Prop() scheduleId?: string

  private contents: Object = {
    id: null,
    type: null,
    tutor: null,
    campus: null,
    period: null,
    date: null,
    from: null,
    to: null,
    memo: null,
    participantTime: null,
  }

  private get isEdit(): boolean {
    return !_.isUndefined(this.scheduleId)
  }

  private get isLesson(): boolean {
    const type: scheduleType = _.get(this.contents, 'type')
    return type === 'lesson' || type === 'subLesson'
  }

  /**
   * スケジュールの時間（分）
   * @returns {number}
   */
  private get scheduleTimeOfMinutes(): number {
    const from: Moment = moment(_.get(this.contents, 'from'))
    const to: Moment = moment(_.get(this.contents, 'to'))
    const timeDifference = to.diff(from, 'minutes')

    if (Math.sign(timeDifference) === -1) return 0;
    return timeDifference
  }

  /**
   * スケジュール参加時間（分）の選択肢
   * @returns {number[]}
   */
  private get participantTimes(): { time: number|null, label: string}[] {
    const maxTime = this.scheduleTimeOfMinutes
    const result = []
    for (let minutes = 0; minutes < maxTime; minutes += 10) {
      result.push({
        time: minutes,
        label: `${minutes} min`,
      })
    }

    return [{
      time: this.scheduleTimeOfMinutes,
      label: 'full',
    }, ..._.orderBy(result, ['time'], ['desc'])]
  }

  private async created() {
    if (this.$route.params.editingSchedule) {
      // もし編集情報があるならそれを表示
      const schedule = JSON.parse(this.$route.params.editingSchedule)
      this.contents = {
        ...schedule,
        time: _.find({ time: schedule.time }),
        date: schedule.date,
        from: moment(schedule.from),
        to: moment(schedule.to),
      }
    } else if (this.scheduleId) {
      await this.$store.dispatch('fetchScheduleById', { id: this.scheduleId })

      const schedule: Schedule = this.$store.getters.getScheduleById(
        this.$route.params.scheduleId,
      )

      this.contents = {
        ...schedule,
        date: schedule.date.toDate(),
        from: schedule.from,
        to: schedule.to,
      }
    }
  }

  @Watch('contents', { deep: true })
  private changedCotents(contents: Schedule) {
    this.$emit('input', contents)
  }
}
</script>

<style scoped>
  .main-content {
    padding: 20px;
    border-radius: 20px;
    margin-bottom:20px;
  }

  .detail-table {
    width:100%;
  }

  .detail-table .detail-item-title {
    font-weight: bold;
    text-align: left;
    margin-bottom: 10px;
  }

  .detail-table .detail-item {
    margin-bottom: 20px;
  }

  .participantTime {
    width: 80px;
  }

  input[type="text"], input[type="date"], select, textarea{
    padding: 5px;
    border: 1px solid #AAAFB6;
    border-radius: 30px;
    width:100%;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
  }

  textarea {
    padding:10px 20px;
  }

  table.detail-table td.plus-container {
    display: flex;
    justify-content: flex-end;
  }

</style>
